"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";

import { isUsableWebSession, useAuthStore } from "@/lib/auth-store";
import { useMounted } from "@/lib/use-mounted";
import type { MarketConfig } from "@/lib/types";

const navItems = [
  { href: "/", label: "Home" },
  { href: "/marketplace", label: "Marketplace" },
  { href: "/buyer-demand", label: "Buyer Needs" },
  { href: "/services", label: "Services" },
  { href: "/videos", label: "Videos" },
  { href: "/communities", label: "Community" },
];

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname.startsWith(href);
}

export function SiteShell({ children, market }: { children: React.ReactNode; market: MarketConfig }) {
  const pathname = usePathname();
  const mounted = useMounted();
  const rawUser = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const rawDemoMode = useAuthStore((state) => state.demoMode);
  const hasSession = mounted
    ? isUsableWebSession({ user: rawUser, accessToken, demoMode: rawDemoMode })
    : false;
  const user = hasSession ? rawUser : null;
  const demoMode = mounted ? rawDemoMode : false;
  const logout = useAuthStore((state) => state.logout);

  return (
    <div className="site">
      <header className="topnav">
        <Link href="/" className="brand">
          <Image
            className="brand-logo"
            src="/brand/skylight-logo-banner.png"
            alt="Skylight"
            width={2239}
            height={411}
            priority
            sizes="(max-width: 720px) 140px, 210px"
          />
        </Link>

        <nav className="navlinks" aria-label="Primary navigation">
          {navItems.map((item) => (
            <Link key={item.href} href={item.href} className={isActive(pathname, item.href) ? "active" : ""}>
              {item.label}
            </Link>
          ))}
        </nav>

        <form action="/marketplace" className="global-search">
          <input name="q" placeholder="Search produce, services, experts..." />
        </form>

        <div className="top-actions">
          <Link href="/services" className="location-pill">
            {market.countryCode} - {market.regionLabelPlural}
          </Link>
          <Link href={user ? "/messages" : "/auth?next=/messages&context=Open%20messages"} className="icon-button" aria-label="Messages">
            Messages
          </Link>
          {user ? (
            <Link href="/activity" className="icon-button" aria-label="My activity">
              Activity
            </Link>
          ) : null}
          {user ? (
            <>
              <Link href="/profile" className="user-chip">
                {user.displayName ?? user.email ?? "Profile"}{demoMode ? " (demo)" : ""}
              </Link>
              <button className="btn btn-ghost btn-small" type="button" onClick={logout}>
                Log out
              </button>
            </>
          ) : (
            <Link href="/auth" className="btn btn-primary btn-small">
              Sign in
            </Link>
          )}
        </div>
      </header>

      <main>{children}</main>

      <footer className="footer">
        <div className="footer-inner">
          <div>
            <div className="brand footer-brand">
              <Image
                className="brand-logo footer-brand-logo"
                src="/brand/skylight-logo-banner.png"
                alt="Skylight"
                width={2239}
                height={411}
                sizes="170px"
              />
            </div>
            <p>Discovery, services, advisory, and agricultural market access for every region Skylight serves.</p>
          </div>
          <div>
            <h4>Platform</h4>
            <Link href="/marketplace">Marketplace</Link>
            <Link href="/buyer-demand">Buyer needs</Link>
            <Link href="/services">Services</Link>
          </div>
          <div>
            <h4>Users</h4>
            <Link href="/profile/farms">My farms</Link>
            <Link href="/activity">My activity</Link>
            <Link href="/profile">Profile</Link>
            <Link href="/settings">Settings</Link>
            <Link href="/messages">Messages</Link>
          </div>
          <div>
            <h4>Market</h4>
            <span>{market.countryName}</span>
            <span>{market.currency}</span>
            <span>{market.paymentLabel}</span>
          </div>
          <div>
            <h4>Legal</h4>
            <Link href="/policy">Policy Center</Link>
            <Link href="/account-deletion-request">Account deletion</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
